/**
 * Admin workspace activity page for /admin/activity route.
 * Per workspace: how much it was used in the chosen period, and when it was last used.
 */

import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Activity } from "lucide-react";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { PageHeader } from "../components/layout/PageHeader";
import { useProfile } from "../contexts/ProfileContext";
import { useAdminWorkspaceActivity } from "../hooks/useAdminWorkspaces";
import { adminRoutes, publicRoutes } from "../lib/routes";
import type { WorkspaceActivity } from "../services/naraDoor";
import { KiiList, type KiiListColumn, type SortState } from "../components/kii";
import { PERIODS, SORT_COLUMNS, lastActivityAt, sortActivity } from "../lib/workspaceActivity";
import { TableRow, TableCell } from "../components/ui/table";

export function AdminActivity() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatDate } = useDateFormatter();
  const { isAdmin, isLoading: isProfileLoading } = useProfile();

  const [periodKey, setPeriodKey] = useState<(typeof PERIODS)[number]["key"]>("days30");
  const [showDev, setShowDev] = useState(false);
  const [sortState, setSortState] = useState<SortState>({ column: "lastActivity", direction: "desc" });

  const days = PERIODS.find((p) => p.key === periodKey)?.days;
  const { data, isLoading, error } = useAdminWorkspaceActivity(days);

  useEffect(() => {
    if (error) console.error("AdminActivity: load failed", error);
  }, [error]);

  const rows = useMemo(
    () => sortActivity((data?.workspaces ?? []).filter((w) => showDev || !w.isDev), sortState),
    [data, showDev, sortState]
  );

  const columns: KiiListColumn[] = SORT_COLUMNS.map((key) => ({
    key,
    label: t(`adminActivity.columns.${key}`),
    sortable: true,
  }));

  // Redirect non-admins to dashboard (after every hook, so the hook order never changes)
  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={publicRoutes.home} replace />;
  }

  const dateText = (when: string | number | null) => (when ? formatDate(when, "date") : "—");
  const countCell = (count: number, last: string | null) => (
    <TableCell>
      <div>{count.toLocaleString()}</div>
      <div className="text-xs text-muted-foreground">{dateText(last)}</div>
    </TableCell>
  );
  const numberCell = (n: number) => <TableCell>{n.toLocaleString()}</TableCell>;

  return (
    <div>
      <PageHeader
        title={t("adminActivity.title")}
        description={t("adminActivity.description")}
        isAdmin
        breadcrumbs={[
          { label: t("adminActivity.breadcrumb.dashboard"), href: publicRoutes.home },
          { label: t("adminActivity.breadcrumb.admin") },
          { label: t("adminActivity.breadcrumb.activity") },
        ]}
      />

      <div className="mb-4 flex flex-wrap items-center gap-4 text-sm">
        <label className="flex items-center gap-2">
          {t("adminActivity.period.label")}
          <select
            className="rounded-md border border-input bg-background px-2 py-1"
            value={periodKey}
            onChange={(e) => setPeriodKey(e.target.value as typeof periodKey)}
          >
            {PERIODS.map((p) => (
              <option key={p.key} value={p.key}>
                {t(`adminActivity.period.${p.key}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={showDev} onChange={(e) => setShowDev(e.target.checked)} />
          {t("adminActivity.showDev")}
        </label>
      </div>

      <KiiList<WorkspaceActivity>
        columns={columns}
        data={rows}
        isLoading={isLoading}
        emptyMessage={error ? t("adminActivity.error.load") : t("adminActivity.empty")}
        emptyIcon={<Activity className="h-10 w-10 text-muted-foreground" />}
        showCount={false}
        sortState={sortState}
        onSortChange={setSortState}
        onRowClick={(w) => navigate(adminRoutes.workspace(w.workspaceId))}
        renderRow={(w) => (
          <TableRow key={w.workspaceId}>
            <TableCell className="font-medium">{w.name || w.workspaceId}</TableCell>
            <TableCell>{dateText(lastActivityAt(w))}</TableCell>
            {numberCell(w.recordings)}
            {countCell(w.transcriptions, w.lastTranscriptionAt)}
            {countCell(w.aiDocuments, w.lastAiDocumentAt)}
            {countCell(w.exports, w.lastExportAt)}
            <TableCell>{dateText(w.lastLoginAt)}</TableCell>
            {numberCell(w.boostsSpent)}
            {numberCell(w.boostsPurchased)}
            {numberCell(w.boostsGranted)}
            {numberCell(w.boostsBalance)}
          </TableRow>
        )}
      />
    </div>
  );
}
